/**
 * Format Pretty Printer — renders navigation logs and flight plan summaries as plain text.
 * Platform-agnostic: pure string formatting.
 */
import type { NavigationLog, FlightPlan } from './types';

export class FormatPrettyPrinter {
  printNavLog(navLog: NavigationLog): string {
    const lines: string[] = [];
    const hdr = this.pad;
    lines.push('┌─────────────────────────────────────────────────────────────────────────────────────────┐');
    lines.push(`│ NAVIGATION LOG — ${navLog.aircraftId}${' '.repeat(Math.max(0, 69 - navLog.aircraftId.length))}│`);
    lines.push('├──────────┬──────────┬──────┬──────┬──────┬──────┬────────┬────────┬────────┬───────────┤');
    lines.push(`│ ${hdr('From', 8)} │ ${hdr('To', 8)} │ ${hdr('TC', 4)} │ ${hdr('MC', 4)} │ ${hdr('HDG', 4)} │ ${hdr('GS', 4)} │ ${hdr('Dist', 6)} │ ${hdr('ETE', 6)} │ ${hdr('Fuel', 6)} │ ${hdr('CumFuel', 9)} │`);
    lines.push('├──────────┼──────────┼──────┼──────┼──────┼──────┼────────┼────────┼────────┼───────────┤');

    for (const leg of navLog.legs) {
      lines.push(
        `│ ${hdr(leg.fromWaypoint, 8)} │ ${hdr(leg.toWaypoint, 8)} │ ${hdr(String(Math.round(leg.trueCourse)) + '°', 4)} │ ${hdr(String(Math.round(leg.magneticCourse)) + '°', 4)} │ ${hdr(String(Math.round(leg.windCorrectedHeading)) + '°', 4)} │ ${hdr(String(Math.round(leg.groundSpeed)), 4)} │ ${hdr(leg.distanceNm.toFixed(1), 6)} │ ${hdr(this.fmtTime(leg.estimatedTimeMinutes), 6)} │ ${hdr(leg.fuelBurnGallons.toFixed(1), 6)} │ ${hdr(leg.cumulativeFuelGallons.toFixed(1), 9)} │`,
      );
    }

    lines.push('├──────────┴──────────┴──────┴──────┴──────┴──────┼────────┼────────┼────────┼───────────┤');
    lines.push(
      `│ TOTALS${' '.repeat(43)}│ ${hdr(navLog.totalDistanceNm.toFixed(1), 6)} │ ${hdr(this.fmtTime(navLog.totalTimeMinutes), 6)} │ ${hdr(navLog.totalFuelGallons.toFixed(1), 6)} │           │`,
    );
    lines.push('└──────────────────────────────────────────────────┴────────┴────────┴────────┴───────────┘');

    return lines.join('\n');
  }

  printFlightPlanSummary(plan: FlightPlan): string {
    const lines: string[] = [];
    lines.push(`Flight Plan: ${plan.name || plan.id}`);
    lines.push(`${plan.departure.identifier} → ${plan.destination.identifier}`);
    lines.push(`Rules: ${plan.flightRules} | Alt: ${plan.cruiseAltitude}ft | TAS: ${plan.trueAirspeed}kt`);
    lines.push(`Distance: ${plan.totalDistanceNm.toFixed(1)} nm | ETE: ${this.fmtTime(plan.totalTimeMinutes)}`);
    if (plan.totalFuelGallons != null) {
      lines.push(`Fuel: ${plan.totalFuelGallons.toFixed(1)} gal`);
    }
    lines.push(`Waypoints: ${plan.waypoints.map(w => w.identifier).join(' → ')}`);
    return lines.join('\n');
  }

  private pad(s: string, w: number): string {
    return s.length >= w ? s.slice(0, w) : s + ' '.repeat(w - s.length);
  }

  private fmtTime(m: number): string {
    const h = Math.floor(m / 60);
    const min = Math.round(m % 60);
    return `${h}:${String(min).padStart(2, '0')}`;
  }
}
